import React, { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const ADD_VALUE = "__add_new_category__";

/**
 * Category dropdown with inline "+ New category" and "Edit" buttons.
 * - `value` is the selected category id.
 * - `onChange(id)` fires when a category is picked or just created.
 * - `categories` is the current list (parent owns it).
 * - `onCategoriesChanged()` is invoked after a create/edit so the parent can refresh.
 */
export default function CategorySelectWithAdd({
    value,
    onChange,
    categories = [],
    onCategoriesChanged,
    placeholder = "Choose category",
    testId = "category-select",
    allowEdit = true,
}) {
    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(null);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    const selected = categories.find((c) => c.id === value);

    const handlePick = (v) => {
        if (v === ADD_VALUE) {
            setName("");
            setOpenCreate(true);
            return;
        }
        onChange(v);
    };

    const create = async () => {
        const trimmed = name.trim();
        if (!trimmed) return toast.error("Name required");
        setBusy(true);
        try {
            const { data } = await api.post("/categories", { name: trimmed, active: true });
            toast.success(`Category “${data.name}” created`);
            setOpenCreate(false);
            setName("");
            await onCategoriesChanged?.();
            onChange(data.id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to create");
        } finally { setBusy(false); }
    };

    const startEdit = () => {
        if (!selected) return;
        setName(selected.name);
        setOpenEdit(selected);
    };

    const saveEdit = async () => {
        if (!openEdit) return;
        const trimmed = name.trim();
        if (!trimmed) return toast.error("Name required");
        setBusy(true);
        try {
            await api.put(`/categories/${openEdit.id}`, {
                name: trimmed,
                description: openEdit.description || "",
                image_url: openEdit.image_url || "",
                active: openEdit.active !== false,
            });
            toast.success("Category renamed");
            setOpenEdit(null);
            setName("");
            await onCategoriesChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update");
        } finally { setBusy(false); }
    };

    return (
        <div className="flex items-center gap-2">
            <Select value={value || ""} onValueChange={handlePick}>
                <SelectTrigger className="rounded-none border-stone-300 flex-1" data-testid={testId}>
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`${testId}-opt-${c.slug || c.id}`}>
                            {c.name}{c.active === false ? " (hidden)" : ""}
                        </SelectItem>
                    ))}
                    <SelectItem value={ADD_VALUE} className="text-sky-700 font-medium" data-testid={`${testId}-add-new`}>
                        + Add new category
                    </SelectItem>
                </SelectContent>
            </Select>
            {allowEdit && selected && (
                <button
                    type="button"
                    onClick={startEdit}
                    className="text-stone-500 hover:text-sky-700 p-2 border border-stone-300 hover:border-sky-700"
                    title={`Rename “${selected.name}”`}
                    data-testid={`${testId}-edit`}
                >
                    <Pencil className="w-3.5 h-3.5" />
                </button>
            )}

            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogContent className="rounded-none max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl">New category</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 mt-2">
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Name</Label>
                        <Input
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
                            placeholder="e.g. Mother Tinctures"
                            className="rounded-none border-stone-300"
                            data-testid="category-create-name"
                        />
                        <p className="text-[11px] text-stone-500">You'll be able to add a description, image and visibility later from <em>Admin → Categories</em>.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenCreate(false)} className="rounded-none">Cancel</Button>
                        <Button onClick={create} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="category-create-save">
                            <Plus className="w-4 h-4 mr-1" />{busy ? "Creating…" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!openEdit} onOpenChange={(o) => { if (!o) setOpenEdit(null); }}>
                <DialogContent className="rounded-none max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl">Rename category</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 mt-2">
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Name</Label>
                        <Input
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }}
                            className="rounded-none border-stone-300"
                            data-testid="category-edit-name"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenEdit(null)} className="rounded-none">Cancel</Button>
                        <Button onClick={saveEdit} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="category-edit-save">
                            {busy ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
